import {
  type AprobarAsesoriaResponse,
  type DesmarcarAutoAprobarResponse,
  type ElementoBloqueante,
  type MarcarAutoAprobarResponse,
  type ProyeccionCumplimientoResponse,
  type SimularAprobarResponse,
  dashboardService,
} from '@/services/dashboard.service';
import { ApiError } from '@/services/api';
import type { EvaluacionAdminResponse } from '@/types/evaluacionAdmin';
import type {
  AsesorEmpresa,
  AsesoriaEnCurso,
  AsesoriaPendiente,
  AsesorMetrics,
  DashboardEmpresa,
  DashboardGlobal,
  DashboardNormaDetalle,
  EmpresaDashboard,
  IndicadoresResumen,
  TendenciaMes,
} from '@/types/types';
import { DASHBOARD_EVENTS, emitDashboardRefresh } from '@/utils/events';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDashboardGlobalResult {
  data: DashboardGlobal | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseDashboardAsesoresResult {
  asesores: AsesorMetrics[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseDashboardEmpresaResult {
  data: DashboardEmpresa | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseDashboardEmpresasResult {
  empresas: EmpresaDashboard[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardGlobal(): UseDashboardGlobalResult {
  // Este hook NO debe hacer redirect, solo manejar el error en el estado
  const [data, setData] = useState<DashboardGlobal | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getGlobal();
      if (response.success) {
        setData(response.data);
      } else {
        // Solo guardar el error, nunca hacer redirect aquí
        setError('Error al cargar el dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar dashboard');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { data, isLoading, error, refetch };
}

export function useDashboardAsesores(): UseDashboardAsesoresResult {
  // Este hook NO debe hacer redirect, solo manejar el error en el estado
  const [asesores, setAsesores] = useState<AsesorMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getAsesores();
      if (response.success) {
        setAsesores(response.data);
      } else {
        // Solo guardar el error, nunca hacer redirect aquí
        setError('Error al cargar asesores');
      }
    } catch (err) {
      // Solo guardar el error, nunca hacer redirect aquí
      setError(err instanceof Error ? err.message : 'Error al cargar asesores');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { asesores, isLoading, error, refetch };
}

export function useDashboardEmpresa(nit: string): UseDashboardEmpresaResult {
  // Este hook NO debe hacer redirect, solo manejar el error en el estado
  const [data, setData] = useState<DashboardEmpresa | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getEmpresa(nit);
      if (response.success) {
        setData(response.data);
      } else {
        // Solo guardar el error, nunca hacer redirect aquí
        setError('Error al cargar datos de empresa');
      }
    } catch (err) {
      // Solo guardar el error, nunca hacer redirect aquí
      setError(err instanceof Error ? err.message : 'Error al cargar empresa');
    } finally {
      setIsLoading(false);
    }
  }, [nit]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { data, isLoading, error, refetch };
}

export function useDashboardEmpresas(): UseDashboardEmpresasResult {
  const [empresas, setEmpresas] = useState<EmpresaDashboard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getEmpresas();
      if (response.success) {
        const payload = response.data;
        if (Array.isArray(payload)) {
          setEmpresas(payload);
        } else if (payload && Array.isArray(payload.empresas)) {
          setEmpresas(payload.empresas);
        } else {
          setEmpresas([]);
          setError('Respuesta inválida al cargar empresas');
        }
      } else {
        setError('Error al cargar empresas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { empresas, isLoading, error, refetch };
}

interface UseAsesorEmpresasResult {
  empresas: AsesorEmpresa[];
  isLoading: boolean;
  error: string | null;
}

export function useAsesorEmpresas(asesorId: string | null): UseAsesorEmpresasResult {
  const [empresas, setEmpresas] = useState<AsesorEmpresa[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!asesorId) {
      setEmpresas([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchEmpresas = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await dashboardService.getEmpresasByAsesor(asesorId);
        if (!cancelled) {
          if (response.success) {
            setEmpresas(response.data);
          } else {
            setError('Error al cargar empresas del asesor');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error al cargar empresas del asesor');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchEmpresas();
    return () => {
      cancelled = true;
    };
  }, [asesorId]);

  return { empresas, isLoading, error };
}

interface UsePendientesVerificacionResult {
  pendientes: AsesoriaPendiente[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePendientesVerificacion(): UsePendientesVerificacionResult {
  const [pendientes, setPendientes] = useState<AsesoriaPendiente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);

  // Solo mostramos el spinner de carga completa la primera vez. En refetches posteriores
  // (p.ej. tras aprobar/rechazar un campo desde la vista de detalle) ya hay datos en pantalla:
  // poner isLoading en true igual desmontaría toda la página (ve el guard en useEvaluacionAdmin).
  const refetch = useCallback(async () => {
    if (!hasLoadedOnceRef.current) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const response = await dashboardService.getPendientesVerificacion();
      if (response.success) {
        setPendientes(response.data);
      } else {
        setError('Error al cargar asesorías pendientes');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar pendientes');
    } finally {
      hasLoadedOnceRef.current = true;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { pendientes, isLoading, error, refetch };
}

interface UseAsesoriasResueltasResult {
  resueltas: AsesoriaPendiente[];
  isLoading: boolean;
  error: string | null;
  load: () => Promise<void>;
}

/** Hook perezoso: no hace fetch al montar. Pensado para pestañas (Aprobadas/Rechazadas)
 * que solo deben cargar datos cuando el usuario las activa por primera vez. */
export function useAsesoriasResueltas(): UseAsesoriasResueltasResult {
  const [resueltas, setResueltas] = useState<AsesoriaPendiente[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const load = useCallback(async () => {
    if (hasLoadedOnceRef.current) return;
    hasLoadedOnceRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getAsesoriasResueltas();
      if (response.success) {
        setResueltas(response.data);
      } else {
        setError('Error al cargar asesorías resueltas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asesorías resueltas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { resueltas, isLoading, error, load };
}

interface UseAsesoriasEnCursoResult {
  asesorias: AsesoriaEnCurso[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAsesoriasEnCurso(): UseAsesoriasEnCursoResult {
  const [asesorias, setAsesorias] = useState<AsesoriaEnCurso[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getAsesoriasEnCurso();
      if (response.success) {
        setAsesorias(response.data);
      } else {
        setError('Error al cargar asesorías en curso');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asesorías en curso');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { asesorias, isLoading, error, refetch };
}

export function useAprobarAsesoria() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const aprobar = useCallback(async (asesoriaId: number): Promise<AprobarAsesoriaResponse | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.aprobarAsesoria(asesoriaId);
      if (response?.success) {
        emitDashboardRefresh();
      }
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al aprobar asesoría');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { aprobar, isLoading, error };
}

/** Marca una asesoría bloqueada solo por la cadena de orden para que se apruebe sola en cuanto
 * la anterior de la empresa se resuelva. Si el backend responde `yaAprobable: true`, es que ya
 * no hace falta marcarla — el caller debería usar `aprobarAsesoria` directo en ese caso. */
export function useMarcarAutoAprobar() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const marcar = useCallback(async (asesoriaId: number): Promise<MarcarAutoAprobarResponse | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.marcarAutoAprobar(asesoriaId);
      if (response?.success) {
        emitDashboardRefresh();
      }
      return response;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al marcar para auto-aprobacion';
      setError(message);
      // ApiError conserva el body del backend (message + elementos bloqueantes) para que el
      // caller pueda mostrarlo, en vez de perderlo al devolver null.
      const body = err instanceof ApiError ? (err.body as { elementos?: ElementoBloqueante[] } | undefined) : undefined;
      return { success: false, message, elementos: body?.elementos };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { marcar, isLoading, error };
}

/** Modo debug (ctrl+.): valida si `aprobarAsesoria` pasaría o no, sin persistir ningún cambio. */
export function useSimularAprobarAsesoria() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const simular = useCallback(async (asesoriaId: number): Promise<SimularAprobarResponse | null> => {
    setIsLoading(true);
    setError(null);
    try {
      return await dashboardService.simularAprobarAsesoria(asesoriaId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al simular la aprobación';
      setError(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { simular, isLoading, error };
}

/**
 * Modo debug (ctrl+.): valida si el flujo completo de "Evaluar Asesoría" (reasignación) pasaría,
 * sin persistir ningún cambio. Endpoint dedicado — a diferencia de `useSimularAprobarAsesoria`,
 * tiene en cuenta que ese flujo limpia los rechazos vigentes antes de aprobar.
 */
export function useSimularEvaluarAsesoria() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const simular = useCallback(async (asesoriaId: number): Promise<SimularAprobarResponse | null> => {
    setIsLoading(true);
    setError(null);
    try {
      return await dashboardService.simularEvaluarAsesoria(asesoriaId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al simular la evaluación';
      setError(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { simular, isLoading, error };
}

export function useDesmarcarAutoAprobar() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const desmarcar = useCallback(async (asesoriaId: number): Promise<DesmarcarAutoAprobarResponse | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.desmarcarAutoAprobar(asesoriaId);
      if (response?.success) {
        emitDashboardRefresh();
      }
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al quitar la marca de auto-aprobacion');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { desmarcar, isLoading, error };
}

export function useRechazarAsesoria() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rechazar = useCallback(async (asesoriaId: number, motivo?: string, permiteSubirEvidenciasNuevas?: boolean): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.rechazarAsesoria(asesoriaId, motivo, permiteSubirEvidenciasNuevas);
      if (response.success) {
        emitDashboardRefresh();
      }
      return response.success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al rechazar asesoría');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { rechazar, isLoading, error };
}

export function useEvaluacionAdmin(asesoriaId: number | null) {
  const [data, setData] = useState<EvaluacionAdminResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dataRef = useRef<EvaluacionAdminResponse | null>(null);
  dataRef.current = data;

  const fetch = useCallback(async () => {
    if (!asesoriaId || asesoriaId <= 0 || isNaN(asesoriaId)) return;
    if (!dataRef.current) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const response = await dashboardService.getEvaluacionAsesoria(asesoriaId);
      if (response.success) {
        setData(response);
      } else {
        setError('Error al cargar evaluación');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar evaluación');
    } finally {
      setIsLoading(false);
    }
  }, [asesoriaId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const handler = () => {
      fetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

export function useEvaluacionActual(nit: string | null) {
  const [data, setData] = useState<EvaluacionAdminResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getEvaluacionActual(nit);
      if (response.success) {
        setData(response);
      } else {
        setError('Error al cargar evaluación actual');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar evaluación');
    } finally {
      setIsLoading(false);
    }
  }, [nit]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const handler = () => {
      fetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

export function useProyeccionCumplimiento(nit: string | null) {
  const [data, setData] = useState<ProyeccionCumplimientoResponse['data'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getProyeccionCumplimiento(nit);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al cargar proyeccion de cumplimiento');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar proyeccion');
    } finally {
      setIsLoading(false);
    }
  }, [nit]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const handler = () => {
      fetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

export function useIndicadoresResumen() {
  const [data, setData] = useState<IndicadoresResumen | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getIndicadoresResumen();
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al cargar resumen de indicadores');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar resumen');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { data, isLoading, error, refetch };
}

export function useDashboardNormasDetalle() {
  const [data, setData] = useState<DashboardNormaDetalle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await dashboardService.getNormasDetalle();
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError('Error al cargar datos de normas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DASHBOARD_EVENTS.REFRESH, handler);
    return () => window.removeEventListener(DASHBOARD_EVENTS.REFRESH, handler);
  }, [refetch]);

  return { data, isLoading, error, refetch };
}

export function useDashboardTrend() {
  const [data, setData] = useState<TendenciaMes[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrend = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dashboardService.getTrend();
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al generar tendencia');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar tendencia');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { data, isLoading, error, fetchTrend };
}
